/**
 * Sticky navigation bar for the landing page.
 *
 * Transparent over the hero section and transitions to a solid primary
 * background on scroll. Uses IntersectionObserver on the hero section
 * element rather than a raw scroll listener for better performance.
 *
 * @module landing/components/LandingNav
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeAwareLogo } from '../../../shared/src/theme/ThemeAwareLogo';

/**
 * Landing page navigation bar.
 *
 * Observes the `#hero` element via IntersectionObserver. When the hero
 * scrolls out of view, the nav transitions to a solid background.
 */
export function LandingNav(): React.JSX.Element {
  const { t } = useTranslation('landing');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const heroElement = document.getElementById('hero');
    if (!heroElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrolled(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(heroElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <nav
      className={`landing-nav${scrolled ? ' landing-nav--scrolled' : ''}`}
      aria-label="Landing page navigation"
    >
      <div className="landing-nav__logo">
        <ThemeAwareLogo height="32px" className="landing-nav__logo-text" />
      </div>
      <div className="landing-nav__actions">
        <a href="/login/" className="ipa-btn ipa-btn--secondary">
          {t('nav.login')}
        </a>
      </div>
    </nav>
  );
}
